/**
 * Thin client wrapper over /viewer-api/user/prefs.
 *
 * Server-side store is a single JSONB blob per OIDC subject, with
 * top-level keys namespaced by the consuming UI surface
 * ("table.worklist", "break_glass", ...). Reads return the merged
 * blob; writes shallow-merge a patch and return the merged result.
 *
 * No PHI flows through this module — UI state only — but every PUT
 * lands a `user.prefs.write` audit row server-side so changes are
 * traceable per 21 CFR Part 11 §11.10(g).
 */
import { auth } from "../auth/store";

const BASE = "/viewer-api/user/prefs";

function authHeaders(): Record<string, string> {
  const s = auth();
  return s ? { Authorization: `Bearer ${s.accessToken}` } : {};
}

/** Fetch the entire prefs blob for the current user. */
export async function getAll(): Promise<Record<string, any>> {
  const rsp = await fetch(BASE, { headers: { Accept: "application/json", ...authHeaders() } });
  if (!rsp.ok) {
    // Treat any non-2xx as "no prefs yet" — first-time users hit a
    // 200 with `{prefs: {}}` from the server, but if the endpoint
    // is unreachable (offline, server boot) the UI should still
    // render with defaults rather than throw.
    return {};
  }
  const j = await rsp.json() as { prefs?: Record<string, any> };
  return j.prefs ?? {};
}

/**
 * Shallow-merge a patch into the user's prefs. Top-level keys in
 * `patch` replace whole sub-objects; absent keys are preserved.
 */
export async function put(patch: Record<string, any>): Promise<Record<string, any>> {
  const rsp = await fetch(BASE, {
    method: "PUT",
    headers: { "Content-Type": "application/json", Accept: "application/json", ...authHeaders() },
    body: JSON.stringify(patch),
  });
  if (!rsp.ok) throw new Error(`prefs.put failed: ${rsp.status}`);
  const j = await rsp.json() as { prefs?: Record<string, any> };
  return j.prefs ?? {};
}
